import { useAuth, useClerk, useUser } from '@clerk/react';
import type { InvitePreviewDto } from '@reelplicate/api-client/model';
import { useAcceptInvite, useGetInvite } from '@reelplicate/api-client/team';
import { getGetMeQueryKey } from '@reelplicate/api-client/users';
import { getListWorkspacesQueryKey } from '@reelplicate/api-client/workspaces';
import { Button, getInitials, Skeleton, Stack, toast, Well } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { type ReactNode, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';

import { AuthBody, AuthCard, AuthHeader } from '@/features/auth/auth-card';
import { withRedirect } from '@/features/auth/redirect-target';
import { fromApiRole, roleName } from '@/features/team/roles';
import { getErrorMessage } from '@/lib/errors';

const statusOf = (error: unknown) =>
  isAxiosError(error) ? error.response?.status : undefined;

/** What went wrong accepting, when it's more than a toast. */
type Problem = { kind: 'wrong-account' } | { kind: 'gone'; message: string };

/** The workspace's initials on a 56px brand tile, like the Auth status icon. */
function WorkspaceTile({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      className="bg-brand-primary text-text-primary text-avatar-56 shadow-sm flex size-14 items-center justify-center rounded-xl"
    >
      {getInitials(name)}
    </span>
  );
}

function GoToAppButton({ variant = 'outline' }: { variant?: 'primary' | 'outline' }) {
  return (
    <Button asChild variant={variant} size="lg" className="w-full">
      <RouterLink to="/">Go to Reelplicate</RouterLink>
    </Button>
  );
}

/** The card for a link that leads nowhere: unknown, revoked or missing token. */
function InvalidInvite() {
  return (
    <AuthCard>
      <Well tone="danger" title="This invite link isn’t valid">
        It may have been revoked or mistyped. Ask whoever invited you to send a new one.
      </Well>
      <GoToAppButton />
    </AuthCard>
  );
}

function InviteSkeleton() {
  return (
    <AuthCard>
      <Stack gap={4} align="center" aria-busy="true" aria-label="Loading invite">
        <Skeleton shape="rectangle" shimmer className="size-14 rounded-xl" />
        <Skeleton className="h-7 w-48" />
        <Skeleton className="w-64" />
        <Skeleton shape="rectangle" shimmer className="h-12 w-full" />
      </Stack>
    </AuthCard>
  );
}

/**
 * Where an invite email's link lands (`/invite?token=…`), signed in or out
 * and outside the onboarding guards - a new invitee skips onboarding once
 * they accept. Shows the workspace, who invited them and as what; signed out
 * it offers sign-up (email pre-filled) or sign-in, both coming back here;
 * signed in it accepts and opens the workspace.
 */
export function InvitePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const token = new URLSearchParams(location.search).get('token') ?? '';
  const here = `${location.pathname}${location.search}`;
  const [problem, setProblem] = useState<Problem | null>(null);

  const invite = useGetInvite(token, {
    query: { enabled: Boolean(token), retry: false, throwOnError: false },
  });
  const accept = useAcceptInvite({ mutation: { meta: { silent: true } } });

  if (!token || statusOf(invite.error) === 404) return <InvalidInvite />;
  if (invite.error) {
    return (
      <AuthCard>
        <Well tone="danger" title="Couldn’t load this invite">
          {getErrorMessage(invite.error)}
        </Well>
        <Button size="lg" className="w-full" onClick={() => void invite.refetch()}>
          Try again
        </Button>
      </AuthCard>
    );
  }
  if (!invite.data || !isLoaded) return <InviteSkeleton />;

  const preview: InvitePreviewDto = invite.data;
  const inviter = preview.inviterName ?? 'whoever invited you';

  const onAccept = async () => {
    setProblem(null);
    try {
      await accept.mutateAsync({ token });
    } catch (error) {
      const status = statusOf(error);
      if (status === 403) setProblem({ kind: 'wrong-account' });
      else if (status === 410) {
        setProblem({ kind: 'gone', message: getErrorMessage(error) });
      } else if (status === 404) await invite.refetch();
      else toast.error(getErrorMessage(error));
      return;
    }
    // They're a member now, with onboarding skipped: reload both before the
    // app's guards read them.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey(), refetchType: 'all' }),
      queryClient.invalidateQueries({
        queryKey: getListWorkspacesQueryKey(),
        refetchType: 'all',
      }),
    ]);
    await navigate('/', { replace: true });
  };

  let body: ReactNode;
  if (preview.status === 'EXPIRED' || problem?.kind === 'gone') {
    // A 410 on accept says which: expired, or used in the meantime.
    body = (
      <>
        <Well
          tone="warning"
          title={problem?.kind === 'gone' ? problem.message : 'This invite has expired'}
        >
          Ask {inviter} for a new one.
        </Well>
        <GoToAppButton />
      </>
    );
  } else if (preview.status === 'ACCEPTED') {
    body = (
      <>
        <Well tone="warning" title="This invite has already been used">
          If it was you, sign in to open the workspace.
        </Well>
        {isSignedIn ? (
          <GoToAppButton variant="primary" />
        ) : (
          <Button asChild size="lg" className="w-full">
            <RouterLink to="/sign-in">Sign in</RouterLink>
          </Button>
        )}
      </>
    );
  } else if (!isSignedIn) {
    body = (
      <>
        <Button asChild size="lg" className="w-full">
          <RouterLink
            to={withRedirect(`/sign-up?email=${encodeURIComponent(preview.email)}`, here)}
          >
            Create account
          </RouterLink>
        </Button>
        <Button asChild variant="outline" size="lg" className="w-full">
          <RouterLink to={withRedirect('/sign-in', here)}>Sign in</RouterLink>
        </Button>
      </>
    );
  } else if (problem?.kind === 'wrong-account') {
    const current = user?.primaryEmailAddress?.emailAddress ?? 'another account';
    body = (
      <>
        <Well tone="warning" title="Wrong account">
          You’re signed in as {current}; this invite is for {preview.email}.
        </Well>
        <Button
          size="lg"
          className="w-full"
          onClick={() => void signOut({ redirectUrl: here })}
        >
          Sign out
        </Button>
      </>
    );
  } else {
    body = (
      <Button
        size="lg"
        className="w-full"
        loading={accept.isPending}
        onClick={() => void onAccept()}
      >
        Accept invitation
      </Button>
    );
  }

  return (
    <AuthCard>
      <Stack gap={4} align="center">
        <WorkspaceTile name={preview.workspaceName} />
        <AuthHeader
          title={`Join ${preview.workspaceName}`}
          subtitle={`Invited by ${inviter} · Role: ${roleName(fromApiRole(preview.role))}`}
        />
      </Stack>
      <AuthBody>
        <p className="text-body-md text-text-secondary text-center">
          This invite is for{' '}
          <span className="text-body-md-strong text-text-primary">{preview.email}</span>
        </p>
        {body}
      </AuthBody>
    </AuthCard>
  );
}
